import React from 'react';

import { useDeletePaymentMethodV2, useShowPaymentMethodV2 } from '@givebacks/api/core';
import { useQueryClient } from '@tanstack/react-query';
import { ButtonMantine, Card, Group, Modal, Stack, Text, Title, notifications } from 'ui';

import { ExistingCreditCard } from '../../Cards';

type RemoveCredentialModalProps = {
  opened: boolean;
  onClose: () => void;
  onBack: () => void;
  credentialId: string;
  header: React.ReactNode;
};

type RemoveCredentialContentProps = {
  onClose: () => void;
  onBack: () => void;
  credentialId: string;
};

const RemoveCredentialContent = ({
  onClose,
  onBack,
  credentialId,
}: RemoveCredentialContentProps) => {
  const queryClient = useQueryClient();

  const { data: credential } = useShowPaymentMethodV2({ uuid: credentialId });
  const { mutate: deletePaymentMethod, isPending: isLoading } = useDeletePaymentMethodV2();

  const handleDelete = () => {
    deletePaymentMethod(credentialId, {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ['user', 'payment_methods'],
        });
        notifications.show({
          message: 'Card has been successfully removed',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        notifications.show({
          message: 'Card could not be removed',
          variant: 'error',
        });
      },
    });
  };

  if (!credential) {
    return null;
  }

  return (
    <Stack>
      <ExistingCreditCard credential={credential} />
      <Card bg="cobalt.0" radius="sm">
        <Stack gap={4}>
          <Title order={4} fz={12}>
            You will no longer...
          </Title>
          <Text fz={12}>Receive any cashback earning when you use this card.</Text>
        </Stack>
      </Card>
      <Group grow>
        <ButtonMantine variant="subtle" onClick={onBack}>
          Cancel
        </ButtonMantine>
        <ButtonMantine color="cerise" onClick={handleDelete} loading={isLoading}>
          Remove Card
        </ButtonMantine>
      </Group>
    </Stack>
  );
};

const RemoveCredentialModal = ({
  opened,
  onClose,
  onBack,
  credentialId,
  header,
}: RemoveCredentialModalProps) => {
  return (
    <Modal opened={opened} onClose={onClose} title={header}>
      <RemoveCredentialContent onClose={onClose} onBack={onBack} credentialId={credentialId} />
    </Modal>
  );
};

export { RemoveCredentialModal };
